'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Import direto, não pelo barrel '@/hooks': o barrel arrasta a árvore inteira
// de hooks para dentro de quem só quer o passe -- e para dentro dos testes
// de quem o consome.
import { useHumanPassGate, type HumanPassStatus } from '@/hooks/useHumanPassGate';

type HumanPassContextValue = {
  status: HumanPassStatus;
  /** Resolve um novo desafio. Usar quando a cota do passe se esgota. */
  refresh: () => void;
};

const HumanPassContext = createContext<HumanPassContextValue>({
  status: 'idle',
  refresh: () => {},
});

/**
 * Resolve o desafio de humanidade uma vez por sessão, no nível da aplicação.
 *
 * É global de propósito. Sete fluxos distintos chamam actions que exigem passe
 * — login, checkout, recuperação de CPF, carteira, validação, perfil e o
 * acordeão de títulos. Montar o widget em cada um significaria que esquecer um
 * quebra aquele fluxo silenciosamente no dia em que a exigência for ligada.
 */
/**
 * Teto para o overlay ficar em pé. É rede de segurança, não fluxo normal: se o
 * desafio não resolveu nesse tempo, alguma coisa saiu do previsto e prender a
 * tela deixou de ajudar.
 */
const MODAL_TIMEOUT_MS = 20_000;

export const HumanPassProvider = ({ children }: { children: ReactNode }) => {
  const { containerRef, status, refresh, isConfigured, needsInteraction } =
    useHumanPassGate();
  const [dismissed, setDismissed] = useState(false);

  // Só vira modal quando há de fato algo para clicar. No caminho comum o
  // desafio resolve sozinho, com tamanho zero, e nada aparece -- prender a tela
  // de todo visitante por um evento raro custaria conversão numa página de
  // venda.
  //
  // 'error' precisa sair daqui: quando o siteverify recusa o token, o widget
  // continua desenhado (mostrando "Sucesso!", porque o desafio em si passou) e
  // o status nunca chega a 'ready'. Sem esta condição o overlay ficava preso
  // para sempre e derrubava o site inteiro -- que é justamente o motivo de eu
  // ter descartado um banner fixo.
  const isModal =
    needsInteraction && !dismissed && status !== 'ready' && status !== 'error';

  // Novo desafio volta a poder abrir o overlay.
  useEffect(() => {
    if (!needsInteraction) {
      setDismissed(false);
    }
  }, [needsInteraction]);

  // Saídas do usuário: Esc ou clique no fundo. Sem elas, qualquer estado que eu
  // não tenha previsto vira tela travada.
  useEffect(() => {
    if (!isModal) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDismissed(true);
      }
    };

    const timeout = setTimeout(() => setDismissed(true), MODAL_TIMEOUT_MS);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isModal]);

  return (
    <HumanPassContext.Provider value={{ status, refresh }}>
      {isConfigured && (
        <>
          {isModal && (
            <button
              type="button"
              aria-label="Fechar o desafio de verificação"
              onClick={() => setDismissed(true)}
              // Botão, e não div: clicar no fundo tem de fechar, e isso precisa
              // funcionar no teclado também. Fechado, o widget volta para o
              // canto e continua utilizável -- não some.
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 70,
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
              }}
            />
          )}
          <div
            ref={containerRef}
            data-cy="human-pass-widget"
            {...(isModal && { role: 'dialog', 'aria-modal': true })}
            // O nó nunca troca de pai: só o estilo muda. Remontá-lo destruiria
            // o widget que o Turnstile desenhou dentro dele.
            //
            // Some assim que o passe sai: resolvido o desafio, o Turnstile
            // troca a caixa pelo selo "Success!" e a mantém desenhada. Sem isto
            // ela fica presa no rodapé pelo resto da sessão. O widget continua
            // montado -- só deixa de ocupar a tela -- para o refresh voltar a
            // exibi-lo quando a cota do passe acabar.
            style={
              isModal
                ? {
                    position: 'fixed',
                    inset: 0,
                    zIndex: 71,
                    display: 'grid',
                    placeItems: 'center',
                    padding: 16,
                  }
                : {
                    position: 'fixed',
                    // Afastado do canto: colado em 0,0 a caixa cobria o botão
                    // de pagamento no mobile.
                    bottom: 16,
                    right: 16,
                    zIndex: 60,
                    // Também some em 'error': o Turnstile deixa a caixa
                    // desenhada com "Sucesso!" mesmo quando o siteverify
                    // recusou o token, e ela ficava encalhada sobre o botão de
                    // compra anunciando um sucesso que não existiu. O refresh
                    // traz de volta quando houver novo desafio.
                    display:
                      status === 'ready' || status === 'error' ? 'none' : undefined,
                  }
            }
          />
        </>
      )}
      {children}
    </HumanPassContext.Provider>
  );
};

export const useHumanPass = (): HumanPassContextValue => useContext(HumanPassContext);
